import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const badgeVariants = cva(
  "h-5 gap-1 rounded-4xl border border-transparent px-2 py-0.5 text-xs font-medium transition-all has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&>svg]:size-3! inline-flex items-center justify-center w-fit whitespace-nowrap shrink-0 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive overflow-hidden group/badge",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground [a]:hover:bg-primary/80",
        secondary: "bg-secondary text-secondary-foreground [a]:hover:bg-secondary/80",
        destructive: "bg-destructive/10 [a]:hover:bg-destructive/20 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 text-destructive dark:bg-destructive/20",
        outline: "border-border text-foreground [a]:hover:bg-muted [a]:hover:text-muted-foreground",
        ghost: "hover:bg-muted hover:text-muted-foreground dark:hover:bg-muted/50",
        link: "text-primary underline-offset-4 hover:underline",
        gray: "bg-gray-500/10 text-gray-600 inset-ring inset-ring-gray-500/20 dark:bg-gray-400/10 dark:text-gray-400 dark:inset-ring-gray-400/20",
        red: "bg-red-500/10 text-red-700 inset-ring inset-ring-red-500/20 dark:bg-red-400/10 dark:text-red-400 dark:inset-ring-red-400/20",
        yellow: "bg-yellow-500/10 text-yellow-800 inset-ring inset-ring-yellow-500/20 dark:bg-yellow-400/10 dark:text-yellow-300 dark:inset-ring-yellow-400/20",
        green: "bg-green-500/10 text-green-700 inset-ring inset-ring-green-500/20 dark:bg-green-400/10 dark:text-green-400 dark:inset-ring-green-500/20",
        blue: "bg-blue-500/10 text-blue-600 inset-ring inset-ring-blue-500/20 dark:bg-blue-400/10 dark:text-blue-400 dark:inset-ring-blue-400/30",
        indigo: "bg-indigo-500/10 text-indigo-600 inset-ring inset-ring-indigo-500/20 dark:bg-indigo-400/10 dark:text-indigo-400 dark:inset-ring-indigo-400/30",
        purple: "bg-purple-500/10 text-purple-600 inset-ring inset-ring-purple-500/20 dark:bg-purple-400/10 dark:text-purple-400 dark:inset-ring-purple-400/30",
        pink: "bg-pink-500/10 text-pink-600 inset-ring inset-ring-pink-500/20 dark:bg-pink-400/10 dark:text-pink-400 dark:inset-ring-pink-400/20",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

function Badge({
  className,
  variant = "default",
  render,
  ...props
}: useRender.ComponentProps<"span"> & VariantProps<typeof badgeVariants>) {
  return useRender({
    defaultTagName: "span",
    props: mergeProps<"span">(
      {
        className: cn(badgeVariants({ className, variant })),
      },
      props
    ),
    render,
    state: {
      slot: "badge",
      variant,
    },
  })
}

export { Badge, badgeVariants }
